import { useNavigate } from 'react-router-dom';
import { Fingerprint, Tablet, QrCode, Camera, ArrowRight, Lock } from 'lucide-react';
import { usePlan } from '@/hooks/usePlan';

const PRODUCTS = [
  {
    id: 'punch-log',
    icon: Fingerprint,
    iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    iconColor: 'text-violet-600',
    label: 'Punch Log',
    description: 'Biometric punch machine logs — view, sync, and process raw IN/OUT punches from hardware attendance devices.',
    to: '/products/punch-log',
    badge: null,
    features: ['Multiple punches per day', 'Device sync simulation', 'OT calculation', 'Anomaly detection'],
  },
  {
    id: 'lens',
    icon: Tablet,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600',
    label: 'Lens Devices',
    description: 'Face-recognition kiosk management — register Lens tablets, rotate tokens, and review device activity logs.',
    to: '/lens',
    badge: null,
    feature: 'selfie_attendance',
    features: ['Device registration', 'Token rotation', 'Activity logs', 'Visitor tracking'],
  },
  {
    id: 'qr-gps',
    icon: QrCode,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600',
    label: 'QR + GPS',
    description: 'Rotating QR code attendance — employees scan a time-limited QR displayed on premises to mark presence with GPS verification.',
    to: null,
    badge: 'Phase 2',
    features: ['30-second rotating QR', 'GPS-bound validation', 'Single-use nonce', 'Anti-spoofing'],
  },
  {
    id: 'camera-gps',
    icon: Camera,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600',
    label: 'Camera + GPS',
    description: 'Selfie attendance from the Staff App — employees snap a photo with GPS location which managers verify on a map.',
    to: null,
    badge: 'Phase 2',
    features: ['Liveness check', 'Face matching', 'Geofence validation', 'Manager verification'],
  },
];

export default function ProductsPage() {
  const navigate = useNavigate();
  const { hasFeature } = usePlan();

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Products</h1>
        <p className="text-[var(--text-secondary)] mt-1 text-sm">
          Hardware and software attendance solutions. Manage devices, sync logs, and configure each channel.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {PRODUCTS.map(product => {
          const Icon = product.icon;
          const locked = product.feature ? !hasFeature(product.feature) : false;
          const coming = !!product.badge;

          return (
            <div
              key={product.id}
              className={`relative flex flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-6 transition-all ${
                coming ? 'opacity-60' : 'hover:shadow-[var(--shadow-md)] hover:border-[var(--accent)]/30'
              }`}>
              {product.badge && (
                <span className="absolute top-4 right-4 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {product.badge}
                </span>
              )}

              <div className={`w-12 h-12 rounded-xl ${product.iconBg} flex items-center justify-center mb-4`}>
                <Icon size={24} className={product.iconColor}/>
              </div>

              <h2 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1.5">{product.label}</h2>
              <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-4 flex-1">{product.description}</p>

              <ul className="space-y-1 mb-5">
                {product.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0"/>
                    {f}
                  </li>
                ))}
              </ul>

              {coming ? (
                <div className="flex items-center gap-2 text-[13px] text-[var(--text-tertiary)] font-medium">
                  <Lock size={13}/>
                  Coming in Phase 2
                </div>
              ) : locked ? (
                <div className="flex items-center gap-2 text-[13px] text-[var(--text-tertiary)] font-medium">
                  <Lock size={13}/>
                  Upgrade plan to unlock
                </div>
              ) : (
                <button
                  onClick={() => navigate(product.to)}
                  className="flex items-center gap-2 text-[13px] font-semibold text-[var(--accent)] hover:gap-3 transition-all group">
                  Open
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5"/>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
